import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFloppyDisk, faFileCsv, faTableCells } from "@fortawesome/free-solid-svg-icons";
import SheetGrid from "./SheetGrid";
import { deserialize, serialize, emptySheetContent } from "./sheetModel";
import { evaluateCells, formatValue, cellName } from "./sheetEngine";

const AUTOSAVE_MS = 1200;

// Build a CSV string for the active sheet (computed/displayed values), trimmed to
// the used range. Quotes fields containing commas/quotes/newlines.
const toCsv = (workbook) => {
  const sheet = workbook.sheets[Math.min(workbook.active || 0, workbook.sheets.length - 1)];
  const values = evaluateCells(sheet.cells);
  let maxR = 0;
  let maxC = 0;
  Object.keys(sheet.cells).forEach((addr) => {
    const m = /^([A-Z]+)(\d+)$/.exec(addr);
    if (!m) return;
    const row = parseInt(m[2], 10);
    let col = 0;
    for (let i = 0; i < m[1].length; i += 1) col = col * 26 + (m[1].charCodeAt(i) - 64);
    if (row > maxR) maxR = row;
    if (col > maxC) maxC = col;
  });
  const esc = (s) => (/[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
  const lines = [];
  for (let r = 0; r < maxR; r += 1) {
    const row = [];
    for (let c = 0; c < maxC; c += 1) row.push(esc(formatValue(values[cellName(r, c)])));
    lines.push(row.join(","));
  }
  return lines.join("\n");
};

const downloadText = (filename, text, mime) => {
  const blob = new Blob([text], { type: mime || "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/**
 * SheetEditor — edits the .sheet file identified by fileId. Loads the workbook
 * JSON from the file's content, autosaves edits back (debounced), Ctrl+S to save
 * now, flush-on-close so nothing is lost. Mirrors NotepadFile.
 */
const SheetEditor = ({ fileId }) => {
  const [name, setName] = useState("Spreadsheet");
  const [workbook, setWorkbook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("saved"); // saved | unsaved | saving
  const timerRef = useRef(null);
  const wbRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { getFile } = await import("../files/filesApi");
        const f = await getFile(fileId);
        if (!alive) return;
        const wb = deserialize(f?.content || emptySheetContent());
        wbRef.current = wb;
        setWorkbook(wb);
        if (f?.name) setName(f.name.replace(/\.sheet$/i, ""));
      } catch (e) {
        if (alive) setWorkbook(deserialize(emptySheetContent()));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [fileId]);

  // Flush a pending autosave when the window closes.
  useEffect(
    () => () => {
      if (timerRef.current && wbRef.current) {
        clearTimeout(timerRef.current);
        import("../files/filesApi")
          .then(({ updateFileContent }) => updateFileContent(fileId, serialize(wbRef.current)))
          .catch(() => {});
      }
    },
    [fileId]
  );

  const persist = async (wb) => {
    setStatus("saving");
    try {
      const { updateFileContent } = await import("../files/filesApi");
      await updateFileContent(fileId, serialize(wb));
      setStatus("saved");
    } catch (e) {
      setStatus("unsaved");
    }
  };

  const onChange = (wb) => {
    wbRef.current = wb;
    setWorkbook(wb);
    setStatus("unsaved");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => persist(wb), AUTOSAVE_MS);
  };

  const saveNow = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (wbRef.current) persist(wbRef.current);
  };

  const onKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && (e.key === "s" || e.key === "S")) {
      e.preventDefault();
      saveNow();
    }
  };

  if (loading) return <div className="webtop-sheet webtop-sheet--msg">Loading…</div>;
  if (!workbook) return <div className="webtop-sheet webtop-sheet--msg">This spreadsheet could not be opened.</div>;

  return (
    <div className="webtop-sheet-app" onKeyDown={onKeyDown}>
      <div className="webtop-sheet__appbar">
        <span className="webtop-sheet__appname">
          <FontAwesomeIcon icon={faTableCells} /> {name}
        </span>
        <span className="webtop-sheet__appspacer" />
        <button
          type="button"
          className="webtop-sheet__appbtn"
          title="Export the active sheet as CSV"
          onClick={() => downloadText(`${name}.csv`, toCsv(wbRef.current), "text/csv;charset=utf-8")}
        >
          <FontAwesomeIcon icon={faFileCsv} /> CSV
        </button>
        <button type="button" className="webtop-sheet__appbtn" title="Save (Ctrl+S)" onClick={saveNow}>
          <FontAwesomeIcon icon={faFloppyDisk} /> Save
        </button>
        <span className="webtop-sheet__appstatus">
          {status === "saving" ? "Saving…" : status === "unsaved" ? "Unsaved" : "Saved"}
        </span>
      </div>
      <SheetGrid workbook={workbook} onChange={onChange} />
    </div>
  );
};

/**
 * SheetsApp — the Webtop "Sheets" window (path /webtop-sheets). With ?fileId it
 * edits that .sheet file; without one it offers to create a new spreadsheet in
 * the user's Personal drive, then edits it.
 */
const SheetsApp = () => {
  const [params] = useSearchParams();
  const paramFileId = params.get("fileId");
  const [localFileId, setLocalFileId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const fileId = paramFileId || localFileId;

  if (fileId) return <SheetEditor fileId={fileId} />;

  const createNew = async () => {
    setCreating(true);
    setError(null);
    try {
      const { createNode } = await import("../files/filesApi");
      const node = await createNode({
        name: "Spreadsheet.sheet",
        type: "textfile",
        scope: "personal",
        parentId: null,
        content: emptySheetContent(),
      });
      if (node?.id) setLocalFileId(node.id);
      else setError("Could not create the spreadsheet.");
    } catch (e) {
      setError(e?.message || "Could not create the spreadsheet.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="webtop-sheet-app webtop-sheet-app--landing">
      <div className="webtop-sheet__landing">
        <FontAwesomeIcon icon={faTableCells} className="webtop-sheet__landingicon" />
        <h2>Sheets</h2>
        <p>Create a new spreadsheet in your Personal drive, or open a .sheet file from This PC.</p>
        {error && <div className="webtop-sheet__landingerr">{error}</div>}
        <button type="button" className="webtop-sheet__landingbtn" onClick={createNew} disabled={creating}>
          {creating ? "Creating…" : "+ New spreadsheet"}
        </button>
      </div>
    </div>
  );
};

export default SheetsApp;
